import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentView } from '@/@diffrag/layout/ContentView'
import { SimpleTable } from '@/@diffrag/table/SimpleTable'
import { api } from '@/api'
import { GetAdminProductsParams } from 'api'
import { useMemo, useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { OffsetPagination } from '@/@diffrag/OffsetPagination'
import { useNavigate } from 'react-router-dom'

const CATEGORY_OPTIONS = [
  { label: '전체', value: undefined },
  { label: '기초케어', value: '기초케어' },
  { label: '진정관리', value: '진정관리' },
  { label: '시술후추천템', value: '시술후추천템' },
]

const STATUS_OPTIONS = [
  { label: '전체', value: undefined },
  { label: '노출', value: 'ACTIVE' },
  { label: '미노출', value: 'INACTIVE' },
]

export default function Products() {
  const page = useQueryValue('page', 1)
  const q = useQueryValue('q', undefined)
  const navigate = useNavigate()
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    undefined,
  )

  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(
    undefined,
  )

  const { data, isFetching } = useApi({
    id: 'productList',
    fn: api().adminProduct.getAdminProducts,
    params: {
      page,
      q,
      category: selectedCategory,
      status: selectedStatus,
    } as GetAdminProductsParams,
  })

  const selectedCategoryLabel = useMemo(() => {
    if (!selectedCategory) {
      return '전체'
    }
    return (
      CATEGORY_OPTIONS.find((item) => item.value === selectedCategory)?.label ||
      '전체'
    )
  }, [selectedCategory])

  const selectedStatusLabel = useMemo(() => {
    if (!selectedStatus) {
      return '전체'
    }
    return (
      STATUS_OPTIONS.find((item) => item.value === selectedStatus)?.label ||
      '전체'
    )
  }, [selectedStatus])

  return (
    <ContentView>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">카테고리 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedCategoryLabel}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="z-50 w-[150px] border bg-white shadow-lg"
            >
              {CATEGORY_OPTIONS.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value || 'all'}
                  checked={selectedCategory === option.value}
                  onCheckedChange={() => setSelectedCategory(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">상태 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedStatusLabel}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="z-50 w-[150px] border bg-white shadow-lg"
            >
              {STATUS_OPTIONS.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value || 'all'}
                  checked={selectedStatus === option.value}
                  onCheckedChange={() => setSelectedStatus(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <SimpleTable
        createHref="/products/edit"
        totalCount={data?.count || 0}
        onRowClick={(row) => {
          navigate(`/products/edit?id=${row.id}`)
        }}
        columns={[
          {
            label: 'NO',
            key: 'id',
            isPrimary: true,
            width: 50,
          },
          {
            label: '상품코드',
            key: 'code',
            isPrimary: true,
            width: 100,
          },
          {
            label: '카테고리',
            key: 'category',
            valueMap: {
              기초케어: '기초케어',
              진정관리: '진정관리',
              시술후추천템: '시술후추천템',
            },
            isPrimary: true,
            width: 120,
          },
          {
            label: '상품명',
            key: 'title',
            isPrimary: true,
            formatter: (value, row) => {
              const titles = [
                { lang: 'ko', title: row.title, label: '한국어' },
                { lang: 'zh-CN', title: row.titleZhcn, label: '简体中文' },
                { lang: 'zh-TW', title: row.titleZhtw, label: '繁體中文' },
                { lang: 'en', title: row.titleEn, label: 'English' },
                { lang: 'ja', title: row.titleJa, label: '日本語' },
                { lang: 'th', title: row.titleTh, label: 'ไทย' },
              ].filter((item) => item.title) // 값이 있는 것만 필터링

              if (titles.length === 0) return '-'
              if (titles.length === 1) return titles[0].title

              return (
                <div className="space-y-1">
                  {titles.map((item) => (
                    <div key={item.lang} className="text-xs">
                      <span className="font-semibold text-blue-600">
                        [{item.label}]
                      </span>{' '}
                      {item.title}
                    </div>
                  ))}
                </div>
              )
            },
          },

          {
            label: '가격',
            key: 'price',
            width: 120,
            formatter: (value) => (value ? `${value.toLocaleString()}원` : '-'),
          },

          {
            label: '상태',
            key: 'status',
            width: 100,
            valueMap: {
              ACTIVE: '노출',
              INACTIVE: '미노출',
            },
          },
          {
            label: '등록일',
            key: 'createDate',
            width: 120,
            formatter: (value) => {
              if (!value) return '-'
              const date = new Date(value)
              return date.toLocaleDateString('ko-KR')
            },
          },
        ]}
        data={data?.rows || []}
        isLoading={isFetching}
        options={{
          disableSort: true,
        }}
      />
      <OffsetPagination page={page} total={data?.count || 0} pageSize={20} />
    </ContentView>
  )
}
